import { useEffect, useState, useMemo } from 'react';
import { Box, Flex, Paragraph, Button } from 'theme-ui';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useSettings } from '@backpackjs/storefront';

import {
  useBreakpointValue,
  useSidebar,
  useSelectedOptions,
  useProductInventoryByHandles,
} from '@hooks';
import { Svg } from '@snippets';

import { ColorOption } from './ColorOption';
import { themed } from './ColorOptions.theme';

export const ColorOptions = themed(({ theme, product }) => {
  const [, { openColorsSidebar }] = useSidebar();
  const [selectedOptions, setSelectedOptions] = useSelectedOptions();
  const colorsPerBp = useBreakpointValue([4, 8, 4, 5, 6]);

  const [maxColors, setMaxColors] = useState(4);
  const [swiper, setSwiper] = useState(null);
  const [isStart, setIsStart] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const settings = useSettings();
  const productFilters = useMemo(() => {
    return (settings?.product?.carouselSwatches?.products || []).reduce(
      (acc, { product: p }) => {
        if (!p?.handle) return acc;
        return [...acc, p.handle];
      },
      []
    );
  }, [settings?.product?.carouselSwatches?.products]);

  const selectedColor = selectedOptions?.color;
  const handles = product.groupOptionsMap.handle;

  const handlesWithInventory =
    useProductInventoryByHandles({
      handles,
    }) || [];

  const carouselEnable = useMemo(() => {
    if (
      productFilters?.length &&
      productFilters?.findIndex((handle) => handle === product.handle) === -1
    ) {
      return false;
    }
    return settings?.product?.carouselSwatches?.enable;
  }, [settings?.product?.carouselSwatches, product?.handle]);
  const defaultParams = {
    slidesPerView: 4.6,
    spaceBetween: 12,
    preventClicksPropagation: false,
    preventClicks: false,
    watchOverflow: true,
    grabCursor: true,
    loop: handlesWithInventory?.length > 4,
    navigation: {
      nextEl: '.slide-button-next',
      prevEl: '.slide-button-prev',
    },
    breakpoints: {
      768: {
        slidesPerView: 6.5,
        loop: handles?.length > 6,
      },
    },
  };
  useEffect(() => {
    setMaxColors(colorsPerBp);
  }, [colorsPerBp]);

  const totalSquares = maxColors + 1;

  return product.isGrouped && handlesWithInventory?.length ? (
    <Box data-comp={ColorOptions.displayName} sx={theme.option}>
      <Flex>
        <Flex sx={theme.header} className="flex-grow">
          <Paragraph sx={theme.name}>Color</Paragraph>
          <Paragraph sx={theme.color}>{selectedColor}</Paragraph>
        </Flex>
        {carouselEnable && (
          <Flex className="items-center gap-6 !mt-4 !mb-2.5">
            <Svg
              alt="chevron"
              viewBox="0 0 24 24"
              src="/svgs/arrow-left.svg#arrow-left"
              onClick={() => swiper.slidePrev()}
              role="button"
              className={`slide-button-prev w-5 h-5 transition-opacity ease-in-out duration-150 ${
                isStart ? 'opacity-50 pointer-events-none' : ''
              }`}
            />

            <Svg
              alt="chevron"
              viewBox="0 0 24 24"
              src="/svgs/arrow-right.svg#arrow-right"
              onClick={() => swiper.slideNext()}
              role="button"
              className={`slide-button-next w-5 h-5 transition-opacity ease-in-out duration-150 ${
                isEnd ? 'opacity-50 pointer-events-none' : ''
              }`}
            />
          </Flex>
        )}
      </Flex>

      {carouselEnable && (
        <Swiper
          onSwiper={setSwiper}
          {...defaultParams}
          onSlideChange={(sw) => {
            setIsStart(sw.isBeginning);
            setIsEnd(sw.isEnd);
          }}
        >
          {(handlesWithInventory || []).map((handle, index) => {
            return (
              <SwiperSlide key={`${handle}-${index}`}>
                <Box>
                  <ColorOption
                    handle={handle?.inventory?.handle}
                    selectedColor={selectedColor}
                    setSelectedOptions={setSelectedOptions}
                    isGrouped
                    inventory={handle?.inventory}
                  />
                </Box>
              </SwiperSlide>
            );
          })}
        </Swiper>
      )}

      {!carouselEnable && (
        <Flex as="ul" sx={theme.values(totalSquares)}>
          {(handlesWithInventory || []).slice(0, maxColors).map((handle) => {
            return (
              <ColorOption
                key={handle?.inventory?.handle}
                handle={handle?.inventory?.handle}
                selectedColor={selectedColor}
                setSelectedOptions={setSelectedOptions}
                isGrouped
                inventory={handle?.inventory}
              />
            );
          })}

          {handlesWithInventory?.length > maxColors && (
            <Box as="li" sx={theme.seeAllWrapper}>
              <Button
                variant="buttons.plain"
                aria-label="See all color options."
                onClick={openColorsSidebar}
                sx={theme.seeAll}
              >
                <Svg
                  src="/svgs/arrow-right.svg#arrow-right"
                  alt="Arrow pointing right"
                  viewBox="0 0 24 24"
                  sx={theme.seeAll.icon}
                />
                <Paragraph sx={theme.seeAll.text}>See All</Paragraph>
              </Button>
            </Box>
          )}
        </Flex>
      )}
    </Box>
  ) : null;
});

ColorOptions.displayName = 'ColorOptions';
